import { Box, Checkbox } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useRecoilValue } from "recoil";
import {
  Dimension,
  DimensionValue,
  listDimensions,
  listDimensionValues,
} from "../../../services/Category";
import { PRICE_STATUS_FILTER_OPTIONS } from "../../../Pricing/utils";
import { granularityAtom } from "../../../Alert/AlertGranularityTabs";
import {
  checkedIcon,
  ComboBoxOption,
  CustomPaper,
} from "../../../Alert/Filter/CreateFilterForm";
import { formInputsStyles } from "../../styles";
import { CustomAutocomplete } from "../../../components/Inputs/CustomAutocomplete";

const LIMIT_TAG = 20;
const MAX_DISPLAYED_OPTIONS = 100;

const ValueOption = <T extends ComboBoxOption>({
  option,
  selected,
}: {
  option: T;
  selected: boolean;
}): JSX.Element => {
  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        height: "15px",
        width: "100%",
      }}
    >
      <Checkbox
        checked={selected}
        checkedIcon={checkedIcon}
        size="small"
        sx={{ mr: 1, height: "10px", width: "10px" }}
      />
      <p
        style={{
          display: "block",
          fontSize: "0.8rem",
          margin: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {option.label}
      </p>
    </div>
  );
};

/**
 * Handle dimension values.
 */
export const DimensionValueList = ({
  dimension,
  onChangeDimensionValues,
}: {
  dimension: Dimension;
  onChangeDimensionValues: (dimensionValue: any) => void;
}): JSX.Element => {
  const inputStyles = formInputsStyles();
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<DimensionValue[]>([]);
  const intl = useIntl();

  const query = useQuery(
    ["dimensions", dimension.id],
    () => listDimensionValues(dimension.id),
    {
      select: (data): DimensionValue[] => {
        let queryData = data.results;

        // When weekday filter is selected translate days name (id dimension 29 = "Jour de semaine")
        if (dimension.id === 29) {
          queryData = queryData.map((day) => {
            return {
              ...day,
              label: intl.formatMessage({ id: day.name.toUpperCase() }),
            };
          });
        }

        // When price status filter is selected hardcode and translate options
        if (dimension.id === 35) {
          queryData = PRICE_STATUS_FILTER_OPTIONS.map((option) => {
            return {
              ...option,
              label: intl.formatMessage({ id: option.label }),
            };
          });
        }

        return queryData;
      },
    }
  );

  const handleChange = (
    _event: React.SyntheticEvent,
    selectedOptions: DimensionValue[]
  ) => {
    setValue(selectedOptions);
    onChangeDimensionValues(selectedOptions);
  };

  useEffect(() => {
    setValue([]);
  }, [dimension.id]);

  return (
    <Box
      className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
    >
      <CustomAutocomplete
        multiple
        disableCloseOnSelect={true}
        limitTags={LIMIT_TAG}
        onChange={handleChange}
        maxDisplayedOptions={MAX_DISPLAYED_OPTIONS}
        fullWidth
        isOptionEqualToValue={(option: DimensionValue, value: DimensionValue) =>
          option.id === value.id
        }
        getOptionLabel={(option: DimensionValue) => option.label}
        label={intl.formatMessage({ id: "CHOICE" })}
        options={query.data || []}
        loading={query.isLoading}
        rootRef={autocompleteRef}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}>
            <ValueOption option={option} selected={selected} />
          </li>
        )}
        slotProps={{
          paper: CustomPaper,
          popper: {
            style: {
              width: autocompleteRef?.current?.clientWidth,
            },
          },
        }}
        value={value}
        slots={{
          paper: CustomPaper,
        }}
      />
    </Box>
  );
};

/**
 * Dimension selector.
 */
export const DimensionFormField = ({
  moduleSlug,
  onChangeDimension,
  onChangeDimensionValues,
}: {
  moduleSlug: string;
  onChangeDimension: (dimension: Dimension | null) => void;
  onChangeDimensionValues: (dimensionValues: any | null) => void;
}) => {
  const inputStyles = formInputsStyles();
  const intl = useIntl();
  const granularityValue = useRecoilValue(granularityAtom);
  const [dimension, setDimension] = useState<Dimension | null>(null);

  const query = useQuery(["dimensions"], () => listDimensions(moduleSlug));

  return (
    <Box className={inputStyles.classes.root} sx={{ display: "flex" }}>
      <Box
        className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
      >
        <CustomAutocomplete
          onChange={(_event, value) => {
            if (value) {
              const v = Array.isArray(value) ? value[0] : value;
              setDimension(v);
              onChangeDimension(v);
            } else {
              setDimension(null);
              onChangeDimension(null);
            }
          }}
          fullWidth
          isOptionEqualToValue={(option: Dimension, value: Dimension) =>
            option?.id === value.id
          }
          getOptionLabel={(option: Dimension) => option.label}
          label={intl.formatMessage({ id: "DIMENSION" })}
          loading={query.isLoading}
          options={query.data?.results || []}
          getOptionDisabled={(option) =>
            moduleSlug === "cockpit"
              ? false
              : option.granularity > granularityValue
          }
        />
      </Box>
      {dimension && (
        <DimensionValueList
          dimension={dimension}
          onChangeDimensionValues={onChangeDimensionValues}
        />
      )}
    </Box>
  );
};
